import { useCallback, useEffect, useState } from 'react';
import { useGlobalContext } from '../../context/GlobalContext';
import { ProductType } from '../../enum/materiais/product-type.enum';
import useFindCursos from './hooks/use-find-cursos.hook';
import useFindCurriculo from './hooks/use-find-curriculo.hook';
import useFindProductStemplay from './hooks/use-find-product-stemplay.hook';
import useSelectTypeMaterial from './hooks/use-select-type-material.hook';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const useMateriais = () => {
  const { setIsGlobalLoading, unidade, usuario } = useGlobalContext();
  const { cursos } = useFindCursos();
  const { curriculos } = useFindCurriculo();
  const { productStemplay } = useFindProductStemplay();
  const { selectTypeMaterial } = useSelectTypeMaterial();
  const { t } = useTranslation('materiais')

  const [searchParams, setSearchParams] = useSearchParams();

  const initialProductType =
    ProductType[searchParams.get('tipo')] || usuario?.acessoB2c ? ProductType.Curriculo : ProductType.ProdutoEscola;

  const [productType, setProductType] = useState<ProductType | string>(
    initialProductType,
  );

  const handleProductTypeChange = (productType: ProductType | string) => {
    setProductType(productType);
    setSearchParams({ tipo: ProductType[productType].toString() });
  };

  const fetchAllMaterialData = useCallback((): void => {
    if (!unidade.id) return;

    setIsGlobalLoading(true);

    Promise.all([
      productStemplay.getData(),
      curriculos.getData(),
    ]).finally(() => setIsGlobalLoading(false));
  }, [
    curriculos.getData,
    productStemplay.getData,
    setIsGlobalLoading,
    unidade.id,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    fetchAllMaterialData();

    return () => controller.abort();
  }, [fetchAllMaterialData]);

  const materiaisTitleBasedOnProductType = (): string => {
    if (productType === ProductType.Curso)
      return `${t('materiais:titleCourse')} (${cursos.data?.totalElements || 0})`;

    if (productType === ProductType.Curriculo)
      return `${t('materiais:titleCurriculums')} (${curriculos.data?.totalElements || 0})`;

    if (productType === ProductType.ProdutoEscola)
      return `${t('materiais:titleProduct')} (${productStemplay?.data?.totalElements || 0})`;

    return t('materiais:titleMaterials');
  };

  const tableHeaderText =
    productType === ProductType.ProdutoEscola ? t('materiais:schoolYear') : t('materiais:chapters');

  return {
    cursos,
    curriculos,
    productStemplay,
    productType,
    handleProductTypeChange,
    materiaisTitleBasedOnProductType,
    initialProductType,
    tableHeaderText,
    selectTypeMaterial,
  };
};

export default useMateriais;
